import { useEffect, useMemo, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { OutwriteApi } from '../services/OutwriteService';
import { groupTasksByGivenId } from '../outwrite/learnerPracticeUtils';
import { LEARNER } from '../outwrite/learnerTokens';

/**
 * Learner landing: pick a chart (given_id) to practice.
 */
export default function OutwriteLearnerHome() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    OutwriteApi.list()
      .then((list) => {
        if (!cancelled) setItems(Array.isArray(list) ? list : []);
      })
      .catch(() => {
        if (!cancelled) setError('Could not load tasks.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const groups = useMemo(() => groupTasksByGivenId(items), [items]);

  return (
    <Box
      sx={{
        minHeight: '100%',
        bgcolor: LEARNER.surfaceContainerLow,
        p: 3,
      }}
    >
      <Box sx={{ maxWidth: 720, mx: 'auto' }}>
        <Typography
          component="h1"
          sx={{
            fontSize: 22,
            fontWeight: 400,
            mb: 1,
            color: LEARNER.onSurface,
          }}
        >
          Outwrite practice
        </Typography>
        <Typography
          sx={{ mb: 3, color: LEARNER.onSurfaceVariant, fontSize: 16 }}
        >
          Choose a graph. You will read it with guided questions, then write a
          short overview.
        </Typography>

        {loading ? (
          <CircularProgress size={28} sx={{ color: LEARNER.primary }} />
        ) : null}
        {error ? (
          <Typography color="error" sx={{ mb: 2 }}>
            {error}
          </Typography>
        ) : null}

        {!loading && !error ? (
          <Stack spacing={1.5}>
            {groups.length === 0 ? (
              <Typography color="text.secondary">No tasks available.</Typography>
            ) : (
              groups.map((g, index) => (
                <Box
                  key={g.givenId}
                  sx={{
                    bgcolor: LEARNER.surface,
                    border: `1px solid ${LEARNER.outlineVariantSoft}`,
                    borderRadius: '12px',
                    px: 2.5,
                    py: 2,
                    display: 'flex',
                    flexWrap: 'wrap',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 1.5,
                  }}
                >
                  <Box>
                    <Typography sx={{ fontWeight: 500, fontSize: 16 }}>
                      Task {index + 1}
                      {g.difficulty != null ? ` · difficulty ${g.difficulty}` : ''}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: 14,
                        color: LEARNER.onSurfaceVariant,
                        fontFamily:
                          'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
                      }}
                    >
                      {g.givenId}
                    </Typography>
                  </Box>
                  <Button
                    component={RouterLink}
                    to={`/outwrite/learn/${encodeURIComponent(g.givenId)}`}
                    variant="contained"
                    disableElevation
                    disabled={!g.preWritingId && !g.overviewId}
                    sx={{
                      height: 40,
                      borderRadius: '20px',
                      textTransform: 'none',
                      fontWeight: 500,
                      bgcolor: LEARNER.primary,
                      '&:hover': { bgcolor: '#1D4785' },
                    }}
                  >
                    Start
                  </Button>
                </Box>
              ))
            )}
          </Stack>
        ) : null}
      </Box>
    </Box>
  );
}
